import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import { createStructuredSelector } from 'reselect';
import { connect, useDispatch } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import { object, string } from 'yup';
import { pickBy, identity, get } from 'lodash';
import { earliestYearOfMusicalWork, emptySelectValue, TP, urlRegex } from '../../constants/consts';
import { createEntity, getDynamicCountriesAction } from '../../containers/App/actions';
import { COMPOSER } from '../../constants/endpointConstants';
import { createComposerAction } from '../../containers/ProductionUpdateSteps/actions';
import EditModeCard, { EditModeCardSection } from '../EditModeCard';
import Button from '../Button';
import TextButton from '../TextButton';
import FormikTextField from '../Fields/TextField';
import FormikAutocompleteSelect from '../Fields/FormikAutocompleteSelect';
import SelectField from '../Fields/SelectField';
import ActionStatusModal from '../ActionStatusModal';
import SubmitModalLayout from '../SubmitModalLayout';
import BirthBlock from '../EditComponents/BirthBlock';
import { createDate } from '../../utils/date';
import { selectAggregationsLoading, selectDynamicCountriesOptions } from '../../containers/App/selectors';

const useStyles = makeStyles(theme => ({
  container: {
    marginBottom: '10px',
  },
  cancelButton: {
    marginLeft: '24px',
  },
  item: {
    maxWidth: '270px',
  },
  form: {
    margin: theme.spacing(2),
  },
}));

const {
  firstName,
  lastName,
  birthDate,
  birthCountry,
  birthCity,
  deathDate,
  deathCountry,
  deathCity,
  nationality,
  gender,
  website,
} = {
  firstName: {
    name: 'first_name',
    label: `${TP}.m_FIRSTNAME`,
  },
  lastName: {
    name: 'last_name',
    label: `${TP}.m_REGISTRATION_LASTNAME`,
  },
  birthDate: {
    day: {
      name: 'day_of_birth',
      value: '',
    },
    month: {
      name: 'month_of_birth',
      value: '',
    },
    year: {
      name: 'year_of_birth',
      value: '',
    },
    name: 'date_of_birth',
  },
  birthCountry: {
    name: 'birthCountry',
    label: `${TP}.m_COUNTRY`,
  },
  birthCity: {
    name: 'birth_city_id',
    label: `${TP}.m_CITY`,
  },
  deathDate: {
    day: {
      name: 'day_of_death',
      value: '',
    },
    month: {
      name: 'month_of_death',
      value: '',
    },
    year: {
      name: 'year_of_death',
      value: '',
    },
    name: 'date_of_death',
  },
  deathCountry: {
    name: 'deathCountry',
    label: `${TP}.m_COUNTRY`,
  },
  deathCity: {
    name: 'death_city_id',
    label: `${TP}.m_CITY`,
  },
  nationality: {
    name: 'nationality_country_id',
    label: `${TP}.FN_NATIONALITY_LABEL`,
  },
  gender: {
    name: 'gender',
    label: `${TP}.FN_GENDER_LABEL`,
  },
  website: {
    name: 'website',
    label: `${TP}.m_WEBSITE`,
  },
};

const AddNewComposerModal = ({ countriesOptions, onClose, loadingFieldname }) => {
  const ADULT_AGE = 18;
  const YEARS_RANGE = 100;
  const currYear = createDate().year();

  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const formRef = useRef();

  const [confirmModalOpened, setConfirmModalOpened] = React.useState(false);

  const postComposer = composer => {
    dispatch(
      createEntity({
        endpoint: COMPOSER,
        sagaRoutine: createComposerAction,
        params: pickBy(composer, identity),
        callback: c => onClose(c),
      }),
    );
  };

  const getCitizenshipCountries = v => {
    dispatch(getDynamicCountriesAction({ fieldName: nationality.name, params: { query: v } }));
  };

  const getInitialFormValue = () => ({
    [firstName.name]: '',
    [lastName.name]: '',
    [birthDate.day.name]: '',
    [birthDate.month.name]: '',
    [birthDate.year.name]: '',
    [birthCity.name]: '',
    [birthCountry.name]: '',
    [deathDate.day.name]: '',
    [deathDate.month.name]: '',
    [deathDate.year.name]: '',
    [deathCity.name]: '',
    [deathCountry.name]: '',
    [nationality.name]: '',
    [gender.name]: emptySelectValue,
    [website.name]: '',
  });
  const confirmAddNewComposer = () => {
    setConfirmModalOpened(false);
    const formValue = formRef.current.values;
    postComposer({
      name: `${formValue[firstName.name]} ${formValue[lastName.name]}`,
      [firstName.name]: formValue[firstName.name],
      [lastName.name]: formValue[lastName.name],
      [birthDate.day.name]: formValue[birthDate.day.name]?.value,
      [birthDate.month.name]: formValue[birthDate.month.name]?.value,
      [birthDate.year.name]: formValue[birthDate.year.name]?.value,
      [birthCity.name]: formValue[birthCity.name]?.value,
      [deathDate.day.name]: formValue[deathDate.day.name]?.value,
      [deathDate.month.name]: formValue[deathDate.month.name]?.value,
      [deathDate.year.name]: formValue[deathDate.year.name]?.value,
      [deathCity.name]: formValue[deathCity.name]?.value,
      [nationality.name]: formValue[nationality.name]?.value,
      [gender.name]: formValue[gender.name]?.value,
      [website.name]: formValue[website.name],
    });
  };

  return (
    <SubmitModalLayout
      isOpened
      onClose={onClose}
      title1={t(`${TP}.FN_ADD_NEW_COMPOSER`)}
      submitFunc={() => {
        get(formRef, 'current.submitForm', () => {})();
      }}
    >
      <EditModeCard className={classes.form}>
        <Formik
          initialValues={getInitialFormValue()}
          onSubmit={(values, { setSubmitting }) => {
            setConfirmModalOpened(true);
            setSubmitting(false);
          }}
          enableReinitialize
          validationSchema={addNewComposerSchema}
          innerRef={formRef}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <EditModeCardSection first>
                <Grid container spacing={3} classes={{ container: classes.container }}>
                  <Grid item lg={3} sm={4} xs={12} classes={{ root: classes.item }}>
                    <Field
                      name={firstName.name}
                      component={FormikTextField}
                      t={t}
                      label={t(firstName.label)}
                      nativeInputProps={{ maxLength: 255 }}
                    />
                  </Grid>
                  <Grid item lg={3} sm={4} xs={12} classes={{ root: classes.item }}>
                    <Field
                      name={lastName.name}
                      component={FormikTextField}
                      t={t}
                      label={t(lastName.label)}
                      nativeInputProps={{ maxLength: 255 }}
                    />
                  </Grid>
                </Grid>
                <Grid item lg={8} sm={10} xs={12}>
                  <BirthBlock
                    title={t(`${TP}.FN_BIRTH_INFO_TITLE`)}
                    setFieldValue={setFieldValue}
                    formValues={values}
                    dateFields={birthDate}
                    country={birthCountry}
                    city={birthCity}
                    minYear={earliestYearOfMusicalWork - YEARS_RANGE - ADULT_AGE}
                    maxYear={currYear - ADULT_AGE}
                  />
                </Grid>
                <Grid item lg={8} sm={10} xs={12}>
                  <BirthBlock
                    title={t(`${TP}.FN_DEATH_INFO_TITLE`)}
                    setFieldValue={setFieldValue}
                    formValues={values}
                    dateFields={deathDate}
                    country={deathCountry}
                    city={deathCity}
                    minYear={earliestYearOfMusicalWork}
                    maxYear={currYear}
                  />
                </Grid>
                <Grid container spacing={3} classes={{ container: classes.container }}>
                  <Grid item lg={3} sm={4} xs={12} classes={{ root: classes.item }}>
                    <Field
                      name={nationality.name}
                      component={FormikAutocompleteSelect}
                      instanceId={nationality.name}
                      label={t(nationality.label)}
                      options={countriesOptions}
                      onInputTextChange={getCitizenshipCountries}
                      isClearable
                      isLoading={loadingFieldname === nationality.name}
                    />
                  </Grid>
                  <Grid item lg={2} sm={3} xs={6}>
                    <Field
                      name={gender.name}
                      component={SelectField}
                      options={[
                        { label: t(`${TP}.FN_MALE_GENDER`), value: 0 },
                        { label: t(`${TP}.FN_FEMALE_GENDER`), value: 1 },
                        { label: t(`${TP}.FN_OTHER_GENDER`), value: 2 },
                      ]}
                      label={t(gender.label)}
                      staticLabel
                      onChange={() => {}}
                    />
                  </Grid>
                </Grid>
                <Grid container classes={{ container: classes.container }}>
                  <Grid item lg={5} sm={6} xs={12}>
                    <Field
                      name={website.name}
                      component={FormikTextField}
                      label={t(website.label)}
                      nativeInputProps={{ maxLength: 255 }}
                    />
                  </Grid>
                </Grid>
              </EditModeCardSection>
            </Form>
          )}
        </Formik>
      </EditModeCard>
      <ActionStatusModal
        isOpened={confirmModalOpened}
        onClose={() => setConfirmModalOpened(null)}
        buttons={[
          <TextButton onClick={() => setConfirmModalOpened(false)}>{t(`${TP}.m_CANCEL`)}</TextButton>,
          <Button onClick={confirmAddNewComposer}>{t(`${TP}.m_CONFIRM`)}</Button>,
        ]}
      >
        <span>{t(`${TP}.FN_NEW_COMPOSER_CONFIRM_TEXT`)}</span>
      </ActionStatusModal>
    </SubmitModalLayout>
  );
};

AddNewComposerModal.propTypes = {
  countriesOptions: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  loadingFieldname: PropTypes.string,
};

const mapStateToProps = createStructuredSelector({
  countriesOptions: selectDynamicCountriesOptions(),
  loadingFieldname: selectAggregationsLoading(),
});

const withConnect = connect(mapStateToProps, null);

export default withConnect(AddNewComposerModal);

const validateBirthDeathDates = (value, context) => {
  const DOB = get(context.parent, birthDate.day.name)?.value;
  const MOB = get(context.parent, birthDate.month.name)?.value;
  const YOB = get(context.parent, birthDate.year.name)?.value;
  const DOD = get(context.parent, deathDate.day.name)?.value;
  const MOD = get(context.parent, deathDate.month.name)?.value;
  const YOD = get(context.parent, deathDate.year.name)?.value;
  if (DOB && MOB && YOB && DOD && MOD && YOD) {
    const birthDateObject = createDate(`${YOB}-${MOB}-${DOB}`);
    const deathDateObject = createDate(`${YOD}-${MOD}-${DOD}`);
    return birthDateObject.isBefore(deathDateObject);
  }
  if (YOB && YOD) {
    return YOB < YOD;
  }
  return true;
};

export const addNewComposerSchema = object().shape({
  [firstName.name]: string()
    .nullable()
    .required(`${TP}.FN_REQUIRED_FIELD`),
  [lastName.name]: string()
    .nullable()
    .required(`${TP}.FN_REQUIRED_FIELD`),
  [birthDate.day.name]: object()
    .nullable()
    .test('', `${TP}.FN_DOB_LESS_DOD`, function test(value) {
      return validateBirthDeathDates(value, this);
    }),
  [website.name]: string()
    .nullable()
    .matches(urlRegex, `${TP}.FN_WEB_LINK_INVALID`),
});
